import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import Modal from './Modal';

interface DialogRequest {
  message: string;
  kind: 'confirm' | 'alert';
  resolve: (confirmed: boolean) => void;
}
interface DialogActions {
  confirm: (message: string) => Promise<boolean>;
  alert: (message: string) => Promise<boolean>;
}
const DialogContext = createContext<DialogActions | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const queue = useRef<DialogRequest[]>([]);
  const [current, setCurrent] = useState<DialogRequest | null>(null);
  const request = useCallback((kind: DialogRequest['kind'], message: string) => (
    new Promise<boolean>((resolve) => {
      const dialog = { kind, message, resolve };
      queue.current.push(dialog);
      if (queue.current.length === 1) setCurrent(dialog);
    })
  ), []);
  const confirm = useCallback((message: string) => request('confirm', message), [request]);
  const alert = useCallback((message: string) => request('alert', message), [request]);
  const finish = useCallback((confirmed: boolean) => {
    queue.current.shift()?.resolve(confirmed);
    setCurrent(queue.current[0] ?? null);
  }, []);
  useEffect(() => () => {
    queue.current.splice(0).forEach((dialog) => dialog.resolve(false));
  }, []);

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      {current?.kind === 'confirm' && <ConfirmDialog
        title="Confirm action" message={current.message} confirmLabel="Confirm"
        onConfirm={() => finish(true)} onCancel={() => finish(false)}
      />}
      {current?.kind === 'alert' && <Modal
        title="Notice" className="modal--confirm" open onClose={() => finish(true)}
        footer={<button type="button" className="primary" onClick={() => finish(true)} autoFocus>OK</button>}
      >{current.message}</Modal>}
    </DialogContext.Provider>
  );
}

export function useDialogs(): DialogActions {
  const dialogs = useContext(DialogContext);
  if (!dialogs) throw new Error('useDialogs must be used within DialogProvider');
  return dialogs;
}
